import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { KeyRound } from "lucide-react";

interface LuxuryLoaderProps {
  variant?: 'inline' | 'card' | 'page' | 'overlay';
  size?: 'sm' | 'md' | 'lg';
  message?: string;
  showMessage?: boolean;
  className?: string;
}

const sizeMap = {
  sm: 24,
  md: 48,
  lg: 64
};

const containerSizeMap = {
  inline: 'h-auto',
  card: 'min-h-[200px]',
  page: 'min-h-[400px]',
  overlay: 'min-h-full'
};

export function LuxuryLoader({ 
  variant = 'inline', 
  size = 'md',
  message = 'Preparing your experience...',
  showMessage = true,
  className 
}: LuxuryLoaderProps) {
  const iconSize = sizeMap[size];
  
  return (
    <div 
      className={cn(
        "flex flex-col items-center justify-center gap-4",
        containerSizeMap[variant],
        variant === 'overlay' && 'absolute inset-0 bg-background/80 backdrop-blur-sm z-50',
        className
      )}
      role="status" 
      aria-live="polite"
    >
      <motion.div
        className="relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Outer glow ring */}
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, hsl(var(--gold) / 0.2) 0%, transparent 70%)',
            filter: 'blur(20px)',
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Rotating key icon */}
        <motion.div
          animate={{ 
            rotate: 360,
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            willChange: 'transform',
          }}
        >
          <KeyRound 
            size={iconSize} 
            className="text-gold drop-shadow-[0_0_12px_hsl(var(--gold)/0.6)]"
            strokeWidth={1.5}
          />
        </motion.div>

        {/* Pulsing center dot */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gold"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [1, 0.5, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>

      {/* Optional message */}
      {showMessage && variant !== 'inline' && (
        <motion.p 
          className="text-sm text-muted-foreground font-light tracking-wide"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          {message}
        </motion.p>
      )}
      
      <span className="sr-only">{message}</span>
    </div>
  );
}

// Convenient wrapper components for common use cases
export function InlineLoader({ className }: { className?: string }) {
  return <LuxuryLoader variant="inline" size="sm" showMessage={false} className={className} />;
}

export function CardLoader({ message, className }: { message?: string; className?: string }) {
  return <LuxuryLoader variant="card" size="md" message={message} className={className} />;
}

export function PageLoader({ message, className }: { message?: string; className?: string }) {
  return <LuxuryLoader variant="page" size="lg" message={message} className={className} />;
}

export function OverlayLoader({ message, className }: { message?: string; className?: string }) {
  return <LuxuryLoader variant="overlay" size="lg" message={message} className={className} />;
}
